<template>
  <div class="data-display-wrapper">
    <!-- 加载状态 -->
    <Spin v-if="loading" fix>
      <div class="loading-text">
        <Icon type="ios-loading" size="30" class="spin-icon-load"></Icon>
        <div>{{ langMap.dashboard_loading[lang] }}</div>
      </div>
    </Spin>

    <!-- 空状态 -->
    <div v-if="!loading && !hasData" class="empty-state">
      <div class="empty-icon-wrapper">
        <div class="hologram-ring"></div>
        <div class="hologram-ring delay-1"></div>
        <div class="hologram-ring delay-2"></div>
        <div class="empty-icon">🔧</div>
      </div>
      <div class="empty-text">{{ langMap.empty[lang] }}</div>
      <div class="empty-subtext">{{ emptyMessage }}</div>
    </div>

    <!-- 数据表格 -->
    <div v-if="!loading && hasData" class="data-content">
      <div class="table-wrapper">
        <Table :columns="columns" :data="tableData" stripe border size="small" :height="tableHeight">
          <template slot-scope="{ row }" slot="checkTime">
            <span class="time-cell">{{ formatDateTime(row.checkTime) }}</span>
          </template>

          <template slot-scope="{ row }" slot="type">
            <span class="type-cell">{{ getTypeText(row.type) }}</span>
          </template>

          <template slot-scope="{ row }" slot="status">
            <span :class="['status-cell', getStatusClass(row.status)]">
              {{ getStatusText(row.status) }}
            </span>
          </template>

          <template slot-scope="{ row }" slot="result">
            <div v-if="row.resultBadges && row.resultBadges.length > 0" class="result-summary-container">
              <div 
                v-for="(badge, index) in row.resultBadges" 
                :key="index"
                :class="['result-badge', badge.type]"
              >
                <Icon :type="badge.icon" size="14" />
                <span class="badge-text">{{ badge.label }}: {{ badge.count }}</span>
              </div>
            </div>
            <div v-else class="no-result-badge">
              <Icon type="ios-time-outline" size="14" />
              <span>{{ row.result || '-' }}</span>
            </div>
          </template>

          <template slot-scope="{ row }" slot="operation">
            <Button 
              type="primary" 
              size="small" 
              :icon="exportingRowId === row.id ? 'ios-loading' : 'ios-download-outline'"
              @click="exportToPDF(row)"
              :title="langMap.dashboard_exportPDF[lang]"
              :loading="exportingRowId === row.id"
              :disabled="exportingRowId === row.id"
              style="padding: 2px 8px; font-size: 11px;"
            >
              {{ exportingRowId === row.id ? langMap.dashboard_exportingPDF[lang] : 'PDF' }}
            </Button>
          </template>
        </Table>
      </div>

      <!-- 自定义分页器 -->
      <div class="pagination-wrapper">
        <div class="cyber-pagination">
          <!-- 总数信息 -->
          <div class="pagination-info">
            <span class="info-label">{{ langMap.dashboard_totalRecords[lang] }}</span>
            <span class="info-value">{{ pagination.total }}</span>
            <span class="info-divider">|</span>
            <span class="info-text">{{ pageRangeText }}</span>
          </div>

          <!-- 页码按钮组 -->
          <div class="pagination-controls">
            <!-- 首页 -->
            <button class="page-btn page-first" :disabled="pagination.current === 1" @click="handlePageChange(1)">
              <span class="btn-icon">《《</span>
            </button>

            <!-- 上一页 -->
            <button class="page-btn page-prev" :disabled="pagination.current === 1"
              @click="handlePageChange(pagination.current - 1)">
              <span class="btn-icon">‹</span>
            </button>

            <!-- 页码按钮 -->
            <div class="page-numbers">
              <button v-for="page in visiblePages" :key="page"
                :class="['page-number', { active: page === pagination.current, ellipsis: page === '...' }]"
                :disabled="page === '...'" @click="page !== '...' && handlePageChange(page)">
                <span class="number-glow" v-if="page === pagination.current"></span>
                {{ page }}
              </button>
            </div>

            <!-- 下一页 -->
            <button class="page-btn page-next" :disabled="pagination.current === totalPages"
              @click="handlePageChange(pagination.current + 1)">
              <span class="btn-icon">›</span>
            </button>

            <!-- 尾页 -->
            <button class="page-btn page-last" :disabled="pagination.current === totalPages"
              @click="handlePageChange(totalPages)">
              <span class="btn-icon">》》</span>
            </button>
          </div>

          <!-- 每页条数选择 -->
          <div class="page-size-selector">
            <span class="selector-label">{{ langMap.dashboard_perPage[lang] }}</span>
            <div class="size-options">
              <button v-for="size in pageSizeOptions" :key="size"
                :class="['size-option', { active: pagination.size === size }]" @click="handlePageSizeChange(size)">
                {{ size }}
              </button>
            </div>
          </div>

          <!-- 快速跳转 -->
          <div class="page-jumper">
            <span class="jumper-label">{{ langMap.dashboard_jumpTo[lang] }}</span>
            <input type="number" class="jumper-input" v-model.number="jumpPage" @keyup.enter="handleJumpPage"
              :placeholder="String(pagination.current)" min="1" :max="totalPages" />
            <button class="jumper-btn" @click="handleJumpPage">
              <span class="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import langMap from '@/utils/language'
import { mapState } from 'vuex'
import api from '@/api'
import { exportSelfCheckToPDF } from '@/utils/pdfExport'

export default {
  name: 'SelfCheckHistoryTable',
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    hasData: {
      type: Boolean,
      default: false
    },
    emptyMessage: {
      type: String,
      default: ''
    },
    tableData: {
      type: Array,
      default: () => []
    },
    pagination: {
      type: Object,
      default: () => ({
        total: 0,
        current: 1,
        size: 20,
        pages: 0
      })
    }
  },
  data() {
    return {
      langMap,
      tableHeight: 500,
      pageSizeOptions: [10, 20, 30, 50],
      jumpPage: null,
      errorCodeDict: {}, // 错误码字典 { '0x10000000': { errNameCn: '未知错误', errNameEn: 'Unknown Error' } }
      exportingRowId: null // 正在导出的行ID
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
    columns() {
      return [
        {
          type: 'expand',
          width: 50,
          render: (h, params) => {
            // 如果没有 items 数据，返回空提示
            if (!params.row.items || params.row.items.length === 0) {
              return h('div', {
                style: {
                  padding: '40px',
                  textAlign: 'center',
                  color: 'rgba(255, 255, 255, 0.4)',
                  background: 'rgba(0, 10, 20, 0.5)',
                  borderRadius: '8px',
                  margin: '10px'
                }
              }, [
                h('Icon', {
                  props: {
                    type: 'ios-information-circle-outline',
                    size: 24
                  },
                  style: {
                    marginBottom: '10px',
                    opacity: 0.5
                  }
                }),
                h('div', this.langMap.dashboard_noDetailData[this.lang])
              ]);
            }

            // 渲染详情面板
            return h('div', {
              class: 'detail-panel',
              style: {
                padding: '12px 15px',
                background: 'linear-gradient(135deg, rgba(0, 10, 20, 0.95) 0%, rgba(0, 20, 40, 0.95) 100%)',
                border: '1px solid rgba(0, 240, 255, 0.2)',
                borderRadius: '6px',
                margin: '8px'
              }
            }, [
              // 详情头部 - 更紧凑
              h('div', {
                class: 'detail-header',
                style: {
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '12px',
                  paddingBottom: '10px',
                  borderBottom: '1px solid rgba(0, 240, 255, 0.2)'
                }
              }, [
                h('span', { class: 'header-icon', style: { fontSize: '18px', marginRight: '8px' } }, '🔍'),
                h('span', {
                  class: 'header-title',
                  style: {
                    fontSize: '14px',
                    fontWeight: '700',
                    color: '#00f0ff',
                    textShadow: '0 0 8px rgba(0, 240, 255, 0.5)',
                    marginRight: '8px'
                  }
                }, this.langMap.dashboard_selfCheckDetail[this.lang]),
                h('span', {
                  class: 'header-count',
                  style: { fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)' }
                }, `(${params.row.items.length} ${this.langMap.dashboard_items[this.lang]})`)
              ]),
              // 详情项列表 - 更紧凑的网格
              h('div', {
                class: 'detail-items',
                style: {
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: '10px'
                }
              }, params.row.items.map((item, idx) => {
                return this.renderDetailItem(h, item, idx);
              }))
            ]);
          }
        },
        {
          title: langMap.dashboard_robotCode[this.lang],
          key: 'robotCode',
          align: 'center',
          width: 150,
          render: (h, params) => {
            return h('span', {
              class: 'robot-code-cell',
              style: {
                fontFamily: 'Courier New, monospace',
                fontSize: '12px',
                color: '#00f0ff',
                fontWeight: '600'
              }
            }, params.row.robotCode || '-');
          }
        },
        {
          title: langMap.dashboard_selfCheckTime[this.lang],
          slot: 'checkTime',
          align: 'center',
          width: 180
        },
        {
          title: langMap.dashboard_selfCheckType[this.lang],
          slot: 'type',
          align: 'center',
          width: 120
        },
        {
          title: langMap.dashboard_selfCheckStatus[this.lang],
          slot: 'status',
          align: 'center',
          width: 180
        },
        {
          title: langMap.dashboard_selfCheckResult[this.lang],
          slot: 'result',
          align: 'center',
          minWidth: 200
        },
        {
          title: langMap.dashboard_operation[this.lang],
          slot: 'operation',
          align: 'center',
          width: 100
        }
      ]
    },
    totalPages() {
      return this.pagination.pages || Math.ceil(this.pagination.total / this.pagination.size) || 1
    },
    pageRangeText() {
      const start = (this.pagination.current - 1) * this.pagination.size + 1
      const end = Math.min(this.pagination.current * this.pagination.size, this.pagination.total)
      return `${start}-${end}`
    },
    visiblePages() {
      const total = this.totalPages
      const current = this.pagination.current
      const delta = 2
      const range = []
      const rangeWithDots = []

      for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
        range.push(i)
      }

      if (current - delta > 2) {
        rangeWithDots.push(1, '...')
      } else {
        rangeWithDots.push(1)
      }

      rangeWithDots.push(...range)

      if (current + delta < total - 1) {
        rangeWithDots.push('...', total)
      } else if (total > 1) {
        rangeWithDots.push(total)
      }

      return rangeWithDots
    }
  },
  mounted() {
    this.calculateTableHeight()
    window.addEventListener('resize', this.calculateTableHeight)
    this.loadErrorCodeDict()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateTableHeight)
  },
  methods: {
    // 加载错误码字典
    loadErrorCodeDict() {
      api.getData('system-server/monitor/dict/errorCode/list', this).then(res => {
        if (res.state == 1 && res.data) {
          // 将数组转换为字典格式 { '0x10000000': { errNameCn: '未知错误', errNameEn: 'Unknown Error' } }
          const dict = {};
          res.data.forEach(item => {
            if (item.errCode) {
              dict[item.errCode] = {
                errNameCn: item.errNameCn,
                errNameEn: item.errNameEn
              };
            }
          });
          this.errorCodeDict = dict;
        }
      }).catch(err => {
        // 错误码字典加载失败（静默处理）
      });
    },

    // 翻译参数key
    translateKey(key) {
      // 如果key是错误码格式（0x开头），统一显示"错误码"
      if (key && key.toString().startsWith('0x')) {
        return this.langMap.errorCode[this.lang];
      }

      const translationKey = `selfCheck_${key}`;
      if (this.langMap[translationKey]) {
        return this.langMap[translationKey][this.lang];
      }
      // 如果没有翻译，返回原始key（首字母大写，下划线转空格）
      return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    // 翻译参数值（处理特殊值）
    translateValue(key, value) {
      // is_online 特殊处理
      if (key === 'is_online' || key === 'ls_online') {
        const translationKey = `selfCheck_is_online_${value}`;
        if (this.langMap[translationKey]) {
          return this.langMap[translationKey][this.lang];
        }
      }

      // is_power_on 特殊处理
      if (key === 'is_power_on') {
        const translationKey = `selfCheck_is_power_on_${value}`;
        if (this.langMap[translationKey]) {
          return this.langMap[translationKey][this.lang];
        }
      }

      // is_enabled 特殊处理
      if (key === 'is_enabled') {
        const translationKey = `selfCheck_is_enabled_${value}`;
        if (this.langMap[translationKey]) {
          return this.langMap[translationKey][this.lang];
        }
      }

      // is_in_mode 特殊处理
      if (key === 'is_in_mode') {
        const translationKey = `selfCheck_is_in_mode_${value}`;
        if (this.langMap[translationKey]) {
          return this.langMap[translationKey][this.lang];
        }
      }

      // 如果value是错误码格式（0x开头），从字典中查找
      if (value && value.toString().startsWith('0x')) {
        const errorInfo = this.errorCodeDict[value];
        if (errorInfo) {
          return this.lang === 'zh' ? errorInfo.errNameCn : errorInfo.errNameEn;
        }
        // 没有找到对应的错误码，返回原始value
        return value;
      }

      // 为特定字段添加单位（根据字段示意图）
      const valueStr = String(value);

      // 时延相关 - 添加 ms 单位
      if (key === 'latency_ms' && !valueStr.includes('ms')) {
        return `${value} ms`;
      }

      // 丢包率 - 添加 % 单位
      if (key === 'packet_loss_rate' && !valueStr.includes('%')) {
        return `${value}%`;
      }

      // CPU使用率 - 添加 % 单位
      if (key === 'cpu_total_usage' && !valueStr.includes('%')) {
        return `${value}%`;
      }

      // 使用率 - 添加 % 单位
      if (key === 'usage_rate' && !valueStr.includes('%')) {
        return `${value}%`;
      }

      // 容量相关 - 添加 GB 单位（如果是纯数字）
      if ((key === 'total_capacity' || key === 'free_capacity' || key === 'total' || key === 'free')
        && /^\d+(\.\d+)?$/.test(valueStr)) {
        return `${value} GB`;
      }

      return value;
    },

    // 渲染详情项
    renderDetailItem(h, item, idx) {
      const levelColors = {
        0: { border: '#39FF14', bg: 'rgba(57, 255, 20, 0.1)', color: '#39FF14' },
        1: { border: '#FFD700', bg: 'rgba(255, 215, 0, 0.1)', color: '#FFD700' },
        2: { border: '#FF8C00', bg: 'rgba(255, 140, 0, 0.1)', color: '#FF8C00' },
        3: { border: '#FF4444', bg: 'rgba(255, 68, 68, 0.1)', color: '#FF4444' }
      };

      const levelColor = levelColors[item.level] || levelColors[0];

      return h('div', {
        key: idx,
        class: ['detail-item', `level-${item.level}`],
        style: {
          background: 'rgba(0, 20, 40, 0.6)',
          borderRadius: '4px',
          padding: '10px 12px',
          borderLeft: `3px solid ${levelColor.border}`,
          transition: 'all 0.3s ease',
          marginBottom: '8px'
        }
      }, [
        // 头部：级别标签 + 设备名 + 设备ID（单行）
        h('div', {
          class: 'item-header',
          style: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: '6px',
            gap: '8px'
          }
        }, [
          // 级别标签
          h('span', {
            class: ['level-badge', `level-${item.level}`],
            style: {
              fontSize: '10px',
              fontWeight: '700',
              padding: '2px 6px',
              borderRadius: '2px',
              textTransform: 'uppercase',
              background: `rgba(${item.level === 0 ? '57, 255, 20' : item.level === 1 ? '255, 215, 0' : item.level === 2 ? '255, 140, 0' : '255, 68, 68'}, 0.2)`,
              color: levelColor.color,
              border: `1px solid ${levelColor.border}`,
              flexShrink: 0
            }
          }, this.getLevelText(item.level)),
          // 设备名称
          h('span', {
            class: 'device-name',
            style: {
              fontSize: '13px',
              fontWeight: '600',
              color: '#00f0ff',
              textShadow: '0 0 5px rgba(0, 240, 255, 0.5)',
              flexShrink: 0
            }
          }, this.lang === 'zh' ? (item.deviceNameCn || item.name) : (item.deviceNameEn || item.name)),
          // 设备ID
          h('span', {
            class: 'device-id',
            style: {
              fontSize: '11px',
              color: 'rgba(255, 255, 255, 0.4)',
              fontFamily: 'Courier New, monospace',
              flexShrink: 0
            }
          }, `(${item.id})`)
        ]),
        // 参数值（紧凑网格）
        item.values && item.values.length > 0 ? h('div', {
          class: 'item-values',
          style: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: '6px',
            marginTop: '6px',
            paddingTop: '6px',
            borderTop: '1px solid rgba(255, 255, 255, 0.08)'
          }
        }, item.values.map((val, vIdx) => {
          // 检查key是否为错误码格式
          const isErrorCode = val.key && val.key.toString().startsWith('0x');

          if (isErrorCode) {
            // 错误码格式：显示两行，占据更多空间
            const errorInfo = this.errorCodeDict[val.key];
            const errorDesc = errorInfo
              ? (this.lang === 'zh' ? errorInfo.errNameCn : errorInfo.errNameEn)
              : val.value;

            return h('div', {
              key: vIdx,
              class: 'value-item error-code-item',
              style: {
                display: 'flex',
                flexDirection: 'column',
                fontSize: '11px',
                padding: '6px 8px',
                background: 'rgba(255, 68, 68, 0.12)',
                borderRadius: '3px',
                border: '1px solid rgba(255, 68, 68, 0.25)',
                gridColumn: 'span 2',
                gap: '3px'
              }
            }, [
              // 第一行：错误码
              h('div', {
                class: 'error-code-line',
                style: {
                  display: 'flex',
                  alignItems: 'center'
                }
              }, [
                h('span', {
                  class: 'value-key',
                  style: {
                    color: 'rgba(255, 255, 255, 0.5)',
                    marginRight: '4px',
                    fontSize: '10px'
                  }
                }, `${this.langMap.errorCode[this.lang]}:`),
                h('span', {
                  class: 'value-value',
                  style: {
                    color: '#FF6B6B',
                    fontFamily: 'Courier New, monospace',
                    fontWeight: '600',
                    fontSize: '11px'
                  }
                }, val.key)
              ]),
              // 第二行：错误码描述
              h('div', {
                class: 'error-desc-line',
                style: {
                  display: 'flex',
                  alignItems: 'center'
                }
              }, [
                h('span', {
                  class: 'value-key',
                  style: {
                    color: 'rgba(255, 255, 255, 0.5)',
                    marginRight: '4px',
                    fontSize: '10px'
                  }
                }, `${this.langMap.dashboard_errorDesc[this.lang]}:`),
                h('span', {
                  class: 'value-value',
                  style: {
                    color: '#00f0ff',
                    fontFamily: 'Courier New, monospace',
                    fontWeight: '600',
                    fontSize: '11px'
                  }
                }, errorDesc)
              ])
            ]);
          } else {
            // 普通参数：单行紧凑显示
            return h('div', {
              key: vIdx,
              class: 'value-item',
              style: {
                display: 'flex',
                alignItems: 'center',
                fontSize: '11px',
                padding: '4px 8px',
                background: 'rgba(0, 0, 0, 0.25)',
                borderRadius: '3px',
                gap: '4px'
              }
            }, [
              h('span', {
                class: 'value-key',
                style: {
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '10px',
                  flexShrink: 0
                }
              }, `${this.translateKey(val.key)}:`),
              h('span', {
                class: 'value-value',
                style: {
                  color: '#00f0ff',
                  fontFamily: 'Courier New, monospace',
                  fontWeight: '600',
                  fontSize: '11px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                },
                attrs: {
                  title: this.translateValue(val.key, val.value)
                }
              }, this.translateValue(val.key, val.value))
            ]);
          }
        })) : null
      ]);
    },

    // 获取级别文本
    getLevelText(level) {
      const levelMap = {
        0: this.langMap.dashboard_selfCheckOk[this.lang],
        1: this.langMap.dashboard_selfCheckWarn[this.lang],
        2: this.langMap.dashboard_selfCheckError[this.lang],
        3: this.langMap.dashboard_selfCheckFatal[this.lang]
      };
      return levelMap[level] || this.langMap.dashboard_levelUnknown[this.lang];
    },

    // 获取状态类名
    getStatusClass(status) {
      if (status === 'completed') return 'success'
      if (status === 'pending') return 'pending'
      return 'failed'
    },

    // 获取状态文本
    getStatusText(status) {
      if (status === 'completed') return this.langMap.dashboard_selfCheckCompleted[this.lang]
      if (status === 'pending') return this.langMap.dashboard_selfCheckPending[this.lang]
      return this.langMap.dashboard_selfCheckFailed[this.lang]
    },

    // 获取触发类型文本
    getTypeText(type) {
      if (type === 1) return this.langMap.dashboard_selfCheckTypeBootup[this.lang]
      if (type === 2) return this.langMap.dashboard_selfCheckTypePlatform[this.lang]
      return '-'
    },

    // 格式化日期时间
    formatDateTime(timestamp) {
      if (!timestamp) return '-'

      if (typeof timestamp === 'string' && timestamp.includes('-')) {
        return timestamp
      }

      const date = new Date(timestamp)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })
    },

    // 计算表格高度
    calculateTableHeight() {
      const windowHeight = window.innerHeight
      const headerHeight = 120
      const paginationHeight = 60
      const padding = 40
      this.tableHeight = windowHeight - headerHeight - paginationHeight - padding - 281
      if (this.tableHeight < 300) {
        this.tableHeight = 300
      }
    },

    // 分页处理
    handlePageChange(page) {
      this.$emit('page-change', page)
    },

    handlePageSizeChange(size) {
      this.$emit('size-change', size)
    },

    // 快速跳转页面
    handleJumpPage() {
      if (!this.jumpPage || this.jumpPage < 1 || this.jumpPage > this.totalPages) {
        this.$Message.warning(
          langMap.dashboard_inputValidPageRange[this.lang].replace('{max}', this.totalPages)
        )
        this.jumpPage = null
        return
      }
      this.handlePageChange(this.jumpPage)
      this.jumpPage = null
    },

    // 导出为PDF（支持中文）
    async exportToPDF(row) {
      // 设置导出状态
      this.exportingRowId = row.id
      
      // 显示加载提示
      const loadingMessage = this.$Message.loading({
        content: this.langMap.dashboard_generatingPDF[this.lang],
        duration: 0 // 持续显示直到手动关闭
      })
      
      try {
        const result = await exportSelfCheckToPDF(row, this)
        
        // 关闭加载提示
        loadingMessage()
        
        if (result.success) {
          this.$Message.success(this.langMap.dashboard_exportSuccess[this.lang])
        } else {
          this.$Message.error(this.langMap.dashboard_exportFailed[this.lang] + ': ' + result.error)
        }
      } catch (error) {
        // 关闭加载提示
        loadingMessage()
        this.$Message.error(this.langMap.dashboard_exportFailed[this.lang] + ': ' + error.message)
      } finally {
        // 清除导出状态
        this.exportingRowId = null
      }
    },

    // 获取级别颜色 (RGB)
    getLevelColor(level) {
      const colorMap = {
        0: [57, 255, 20],   // 绿色
        1: [255, 215, 0],   // 黄色
        2: [255, 140, 0],   // 橙色
        3: [255, 68, 68]    // 红色
      }
      return colorMap[level] || [0, 0, 0]
    },

    // 翻译key为英文（用于PDF导出）
    translateKeyToEnglish(key) {
      const keyMap = {
        'is_online': 'Is Online',
        'ls_online': 'Is Online',
        'is_power_on': 'Is Power On',
        'is_enabled': 'Is Enabled',
        'is_in_mode': 'Is In Mode',
        'latency_ms': 'Latency',
        'packet_loss_rate': 'Packet Loss Rate',
        'total_logical_cores': 'Total Cores',
        'cpu_total_usage': 'CPU Usage',
        'total_capacity': 'Total Capacity',
        'free_capacity': 'Free Capacity',
        'usage_rate': 'Usage Rate',
        'total': 'Total',
        'free': 'Free',
        'devices_total_count': 'Total Devices',
        'disconnected_devices_count': 'Disconnected Devices',
        'param_errors_count': 'Param Errors',
        'devices_errors_count': 'Device Errors'
      }
      return keyMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }
  }
}
</script>

<style lang="less" scoped>
// 结果摘要容器
.result-summary-container {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

// 结果标签
.result-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  
  .badge-text {
    font-family: 'Courier New', monospace;
  }
  
  // 正常状态
  &.ok {
    background: rgba(57, 255, 20, 0.15);
    border: 1px solid rgba(57, 255, 20, 0.4);
    color: #39FF14;
  }
  
  // 警告状态
  &.warn {
    background: rgba(255, 215, 0, 0.15);
    border: 1px solid rgba(255, 215, 0, 0.4);
    color: #FFD700;
  }
  
  // 错误状态
  &.error {
    background: rgba(255, 140, 0, 0.15);
    border: 1px solid rgba(255, 140, 0, 0.4);
    color: #FF8C00;
  }
  
  // 严重状态
  &.fatal {
    background: rgba(255, 68, 68, 0.15);
    border: 1px solid rgba(255, 68, 68, 0.4);
    color: #FF4444;
  }
}

.no-result-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.4);
  
  i {
    color: rgba(255, 255, 255, 0.3);
  }
  
  span {
    font-family: 'Courier New', monospace;
  }
}

// 操作按钮样式
/deep/ .ivu-btn-small {
  padding: 2px 8px !important;
  font-size: 11px !important;
  border-radius: 4px;
  height: 24px !important;
  line-height: 20px !important;
  
  &.ivu-btn-primary {
    background: linear-gradient(135deg, rgba(0, 240, 255, 0.2) 0%, rgba(0, 200, 255, 0.2) 100%);
    border: 1px solid rgba(0, 240, 255, 0.5);
    color: #00f0ff;
    transition: all 0.3s ease;
    
    &:hover:not(:disabled) {
      background: linear-gradient(135deg, rgba(0, 240, 255, 0.3) 0%, rgba(0, 200, 255, 0.3) 100%);
      box-shadow: 0 0 10px rgba(0, 240, 255, 0.5);
      transform: translateY(-1px);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
  
  // 加载图标样式
  .ivu-icon-ios-loading {
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.no-result {
  color: rgba(255, 255, 255, 0.3);
  font-size: 12px;
}

.data-cell {
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: #00f0ff;
  text-shadow: 0 0 5px rgba(0, 240, 255, 0.6);
}

.robot-code-cell {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #00f0ff;
  font-weight: 600;
  text-shadow: 0 0 5px rgba(0, 240, 255, 0.4);
}

.time-cell {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
}

.type-cell {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}

.status-cell {
  font-family: 'Courier New', monospace;
  font-weight: 700;
  font-size: 13px;
  padding: 4px 12px;
  border-radius: 4px;
  display: inline-block;

  &.success {
    color: #39FF14;
    background: rgba(57, 255, 20, 0.15);
    border: 1px solid rgba(57, 255, 20, 0.4);
    text-shadow: 0 0 5px rgba(57, 255, 20, 0.6);
  }

  &.pending {
    color: #FFD700;
    background: rgba(255, 215, 0, 0.15);
    border: 1px solid rgba(255, 215, 0, 0.4);
    text-shadow: 0 0 5px rgba(255, 215, 0, 0.6);
  }

  &.failed {
    color: #FF4444;
    background: rgba(255, 68, 68, 0.15);
    border: 1px solid rgba(255, 68, 68, 0.4);
    text-shadow: 0 0 5px rgba(255, 68, 68, 0.6);
  }
}

.detail-btn {
  color: #00f0ff;
  font-size: 12px;

  &:hover {
    color: #00d4ff;
  }
}

.no-detail {
  color: rgba(255, 255, 255, 0.3);
}

// 详情面板样式
.detail-panel {
  padding: 20px;
  background: linear-gradient(135deg, rgba(0, 10, 20, 0.95) 0%, rgba(0, 20, 40, 0.95) 100%);
  border: 1px solid rgba(0, 240, 255, 0.2);
  border-radius: 8px;
  margin: 10px;

  .detail-header {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 2px solid rgba(0, 240, 255, 0.3);

    .header-icon {
      font-size: 24px;
      margin-right: 10px;
    }

    .header-title {
      font-size: 16px;
      font-weight: 700;
      color: #00f0ff;
      text-shadow: 0 0 10px rgba(0, 240, 255, 0.6);
      margin-right: 10px;
    }

    .header-count {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.6);
    }
  }

  .detail-items {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
    gap: 15px;
  }

  .detail-item {
    background: rgba(0, 20, 40, 0.6);
    border-radius: 6px;
    padding: 15px;
    border-left: 4px solid;
    transition: all 0.3s ease;

    &.level-0 {
      border-left-color: #39FF14;

      &:hover {
        background: rgba(57, 255, 20, 0.1);
        box-shadow: 0 0 15px rgba(57, 255, 20, 0.3);
      }
    }

    &.level-1 {
      border-left-color: #FFD700;

      &:hover {
        background: rgba(255, 215, 0, 0.1);
        box-shadow: 0 0 15px rgba(255, 215, 0, 0.3);
      }
    }

    &.level-2 {
      border-left-color: #FF8C00;

      &:hover {
        background: rgba(255, 140, 0, 0.1);
        box-shadow: 0 0 15px rgba(255, 140, 0, 0.3);
      }
    }

    &.level-3 {
      border-left-color: #FF4444;

      &:hover {
        background: rgba(255, 68, 68, 0.1);
        box-shadow: 0 0 15px rgba(255, 68, 68, 0.3);
      }
    }

    .item-header {
      margin-bottom: 12px;

      .item-title {
        display: flex;
        align-items: center;
        margin-bottom: 8px;

        .level-badge {
          font-size: 11px;
          font-weight: 700;
          padding: 3px 8px;
          border-radius: 3px;
          margin-right: 10px;
          text-transform: uppercase;

          &.level-0 {
            background: rgba(57, 255, 20, 0.2);
            color: #39FF14;
            border: 1px solid rgba(57, 255, 20, 0.4);
          }

          &.level-1 {
            background: rgba(255, 215, 0, 0.2);
            color: #FFD700;
            border: 1px solid rgba(255, 215, 0, 0.4);
          }

          &.level-2 {
            background: rgba(255, 140, 0, 0.2);
            color: #FF8C00;
            border: 1px solid rgba(255, 140, 0, 0.4);
          }

          &.level-3 {
            background: rgba(255, 68, 68, 0.2);
            color: #FF4444;
            border: 1px solid rgba(255, 68, 68, 0.4);
          }
        }

        .device-name {
          font-size: 14px;
          font-weight: 600;
          color: #00f0ff;
          text-shadow: 0 0 5px rgba(0, 240, 255, 0.5);
        }

        .device-id {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.5);
          margin-left: 8px;
          font-family: 'Courier New', monospace;
        }
      }

      .item-message {
        font-size: 13px;
        color: rgba(255, 255, 255, 0.8);
        line-height: 1.5;
        padding-left: 5px;
      }
    }

    .item-values {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
      gap: 8px;
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid rgba(255, 255, 255, 0.1);

      .value-item {
        display: flex;
        align-items: center;
        font-size: 12px;
        padding: 5px 10px;
        background: rgba(0, 0, 0, 0.3);
        border-radius: 4px;

        .value-key {
          color: rgba(255, 255, 255, 0.6);
          margin-right: 6px;
          font-weight: 500;
        }

        .value-value {
          color: #00f0ff;
          font-family: 'Courier New', monospace;
          font-weight: 600;
          text-shadow: 0 0 3px rgba(0, 240, 255, 0.4);
        }
      }
    }
  }
}

// 空详情面板样式
.no-detail-panel {
  padding: 40px;
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  background: rgba(0, 10, 20, 0.5);
  border-radius: 8px;
  margin: 10px;

  i {
    margin-bottom: 10px;
    opacity: 0.5;
  }

  span {
    font-size: 14px;
  }
}
</style>
